import { compact, isEmpty, isNil, omitBy } from 'lodash';

import { common, model } from '@gosupersimple/types';

import {
  ExplorationFragmentFragment,
  MetricFragmentFragment,
  ModelFragmentFragment,
  PipelineOperator,
  QueryPipeline,
  RecordTypeFragmentFragment,
  AlertConfiguration as GraphQLAlertConfiguration,
  ConversationMessage,
  ConversationExplorationMessage,
  ExplorationDataFragmentFragment,
  Conversation,
} from '@/generated/graphql';

import {
  AggregatedVisualisation,
  ChatExploration,
  ChatMessage,
  ChatUserMessage,
  CellViewOptions,
  ConversationJobProgress,
  DereferencedPipeline,
  Exploration,
  Fields,
  Metric,
  Model,
  Pipeline,
  PipelineOperation,
  Visualisation,
  cellViewOptions,
  timeAggregationPeriodZod,
  variableDefinition,
  visualisationZod,
} from './types';
import { AlertConfiguration } from '../settings/types';

import {
  visualisationKeysFromAggregation,
  ensureValidSeries,
} from './components/visualisation/utils';

const parseCellViewOptions = (viewOptions: unknown): CellViewOptions | undefined => {
  if (viewOptions === null || viewOptions === undefined) {
    return undefined;
  }

  return cellViewOptions.parse(viewOptions);
};

export const convertRecordTypeTypes = (
  input?: RecordTypeFragmentFragment[],
): Fields | undefined => {
  return input?.map((i) => ({
    key: i.key,
    type: convertPropertyValueType(i.type),
    name: i.name,
    pk: i.pk ?? undefined,
    relation: !isNil(i.relation)
      ? {
          modelId: i.relation.modelId,
          key: i.relation.key,
          name: i.relation.name,
        }
      : undefined,
    model: !isNil(i.model)
      ? {
          modelId: i.model.modelId,
          name: i.model.name,
          propertyKey: i.model.propertyKey,
        }
      : undefined,
    precision: timeAggregationPeriodZod
      .optional()
      .catch(() => undefined)
      .parse(i.precision),
  }));
};

export const convertMetricTypes = (input?: MetricFragmentFragment[]): Metric[] | undefined => {
  return input?.map((i) => ({
    metricId: i.metricId,
    name: i.name,
    description: i.description ?? undefined,
    definition: i.definition,
  }));
};

export const convertModelTypes = (input?: ModelFragmentFragment[]): Model[] | undefined => {
  return input?.map((i) => ({
    modelId: i.modelId,
    name: i.name,
    description: i.description ?? undefined,
    primaryKey: i.primaryKey,
    labels: i.labels,
    properties: i.properties.map((p) => ({
      name: p.name,
      type: convertPropertyValueType(p.type) ?? ('String' as const),
      format: convertPropertyValueFormat(p.format),
      key: p.key,
      description: p.description ?? undefined,
    })),
    relations: i.relations.map((r) => {
      return {
        key: r.key,
        modelId: r.modelId,
        type: r.type,
        name: r.name,
        description: r.description ?? undefined,
        joinStrategy:
          !isNil(r.joinStrategy.joinKeyOnBase) && !isNil(r.joinStrategy.joinKeyOnRelated)
            ? {
                joinKeyOnBase: r.joinStrategy.joinKeyOnBase,
                joinKeyOnRelated: r.joinStrategy.joinKeyOnRelated,
                through: !isNil(r.joinStrategy.through)
                  ? {
                      modelId: r.joinStrategy.through.modelId,
                      joinKeyToBase: !isNil(r.joinStrategy.through.joinKeyToBase)
                        ? r.joinStrategy.through.joinKeyToBase
                        : undefined,
                      joinKeyToRelated: !isNil(r.joinStrategy.through.joinKeyToRelated)
                        ? r.joinStrategy.through.joinKeyToRelated
                        : undefined,
                    }
                  : undefined,
              }
            : !isNil(r.joinStrategy.steps)
              ? {
                  steps: r.joinStrategy.steps.map((step) => ({
                    relationKey: step.relationKey,
                  })),
                }
              : !isNil(r.joinStrategy.sql)
                ? { sql: r.joinStrategy.sql }
                : undefined,
      };
    }),
    semantics: i.semantics,
  }));
};

const convertPipelineOperationTypes = (operation: PipelineOperator): PipelineOperation =>
  omitBy(
    {
      operation: operation.operation,
      parameters: operation.parameters,
      disabled: operation.disabled,
    },
    isNil,
  ) as PipelineOperation;

const convertPipelineTypes = (input?: QueryPipeline): Pipeline =>
  omitBy(
    {
      baseModelId: input?.baseModelId,
      operations: (input?.operations ?? []).map(convertPipelineOperationTypes),
      pipelineId: input?.pipelineId,
      parentId: input?.parentId,
    },
    isNil,
  ) as Pipeline;

export const convertVisualisation = (input: any): Visualisation => {
  const sanitizedInput = {
    ...input,
    aggregation: input.aggregation ?? undefined,
    mainAxisKey: input.mainAxisKey ?? undefined,
    sort: input.sort ?? undefined,
  };

  const {
    aggregationType,
    aggregationTarget,
    groupByField,
    timeGranularity,
    viewOptions,
    aggregations,
    groups,
    valueKeys,
  } = sanitizedInput;

  if (!isNil(valueKeys)) {
    return visualisationZod.parse({
      ...sanitizedInput,
      viewOptions: {
        ...viewOptions,
        ...{
          series: ensureValidSeries(viewOptions?.series),
        },
      },
    });
  }

  // If aggregations are provided, we assume that the visualisation is already using the new schema
  if (!isNil(aggregations)) {
    const { valueKeys: generatedValueKeys } = visualisationKeysFromAggregation({
      aggregations,
      groups,
    });
    const newViewOptions = {
      ...viewOptions,
      ...(generatedValueKeys.length > 0
        ? {
            series: ensureValidSeries(viewOptions?.series),
          }
        : {}),
    };
    const aggregation = {
      aggregations,
      groups,
    };
    return aggregations.length > 0
      ? {
          aggregation,
          ...visualisationKeysFromAggregation(aggregation),
          viewOptions: newViewOptions,
        }
      : {
          valueKeys: [],
          viewOptions: newViewOptions,
        };
  }

  // TODO: Check if this can be removed
  // Migration logic for old visualisations
  const fallbackVisualisation: AggregatedVisualisation = {
    aggregation: {
      aggregations: [
        {
          type: 'count',
          property: {
            key: 'count',
            name: 'count',
          },
        },
      ],
      groups: [{ key: 'tmp' }], // tmp is replaced with default grouping in ensure valid groupings
    },
    mainAxisKey: 'tmp',
    valueKeys: ['count'],
    viewOptions: {
      ...viewOptions,
      series: ensureValidSeries(viewOptions?.series),
    },
  };

  if (aggregationType === null) {
    return fallbackVisualisation;
  }

  const aggregation = {
    aggregations: [
      {
        type: aggregationType,
        ...(isNil(aggregationTarget) ? {} : { key: aggregationTarget }),
        property: {
          key: aggregationType,
          name: aggregationType,
        },
      },
    ],
    groups: [
      {
        key: groupByField ?? 'tmp', // tmp is replaced with default grouping in ensure valid groupings
        ...(isNil(timeGranularity) ? {} : { fill: true, precision: timeGranularity }),
      },
    ],
  };

  const visualisation = visualisationZod.catch(fallbackVisualisation).parse({
    aggregation,
    ...visualisationKeysFromAggregation(aggregation),
    viewOptions: {
      ...viewOptions,
      series: ensureValidSeries(viewOptions?.series),
    },
  });

  return visualisation;
};

export const convertExplorationTypes = (
  input?: ExplorationFragmentFragment | ExplorationDataFragmentFragment | null,
): Exploration | undefined =>
  isNil(input)
    ? undefined
    : {
        ...(input.schemaVersion === '1.0.0' ? { schemaVersion: '1.0.0' } : {}),
        explorationId: input.explorationId,
        name: input.name,
        description: input.description ?? undefined,
        labels: input.labels,
        parameters: input.parameters.map(({ key, modelId }) => ({ key, modelId })),
        createdAt: input.__typename === 'Exploration' ? input.createdAt : undefined,
        createdBy: input.__typename === 'Exploration' ? input.createdBy : undefined,
        updatedAt: input.__typename === 'Exploration' ? input.updatedAt : undefined,
        updatedBy: input.__typename === 'Exploration' ? input.updatedBy : undefined,
        view: {
          canvas: input.view.canvas ?? undefined,
          tabs: input.view.tabs ?? undefined,
          cells: input.view.cells.map((c) => {
            switch (c.__typename) {
              case 'RecordsViewCell':
                return {
                  id: c.id,
                  kind: 'records',
                  title: c.title,
                  excludeProperties: c.excludeProperties,
                  pipeline: convertPipelineTypes(c.pipeline),
                  visualisations: (c.visualisations ?? []).map(convertVisualisation),
                  sort: (c.sort ?? []).map(({ key, direction }) => ({
                    key,
                    direction: direction === 'DESC' ? 'DESC' : 'ASC',
                  })),
                  viewOptions: parseCellViewOptions(c.viewOptions),
                };
              case 'FunnelViewCell':
                return {
                  id: c.id,
                  kind: 'funnel',
                  title: c.title,
                  pipeline: convertPipelineTypes(c.pipeline),
                  viewOptions: parseCellViewOptions(c.viewOptions),
                };
              case 'CohortViewCell':
                return {
                  id: c.id,
                  kind: 'cohort',
                  title: c.title,
                  pipeline: convertPipelineTypes(c.pipeline),
                  viewOptions: parseCellViewOptions(c.viewOptions),
                };
              case 'VariableViewCell':
                return {
                  id: c.id,
                  kind: 'variable',
                  definition: variableDefinition.parse(c.definition),
                  viewOptions: parseCellViewOptions(c.viewOptions),
                };
              case 'SqlViewCell':
                return {
                  id: c.id,
                  kind: 'sql',
                  title: c.title ?? '',
                  pipeline: convertPipelineTypes(c.pipeline),
                  visualisations: (c.visualisations ?? []).map(convertVisualisation),
                  viewOptions: parseCellViewOptions(c.viewOptions),
                };
              case 'PythonViewCell':
                return {
                  id: c.id,
                  kind: 'python',
                  title: c.title ?? '',
                  pipeline: convertPipelineTypes(c.pipeline),
                  viewOptions: parseCellViewOptions(c.viewOptions),
                };
              case 'TextViewCell':
                return {
                  id: c.id,
                  kind: 'text',
                  title: c.title ?? '',
                  content: c.content ?? '',
                  viewOptions: parseCellViewOptions(c.viewOptions),
                };
              case 'ChatViewCell':
                return {
                  id: c.id,
                  kind: 'text',
                  messages: c.messages,
                  viewOptions: parseCellViewOptions(c.viewOptions),
                };
              case 'ConversationViewCell':
                return {
                  id: c.id,
                  kind: 'conversation',
                  conversationId: c.conversationId,
                };
              default:
                throw new Error(`Unknown cell type: ${c.__typename}`);
            }
          }),
          options: input.view.options ?? undefined,
        },
      };

export const convertExplorationsArrayTypes = (
  input: ExplorationFragmentFragment[] | ExplorationDataFragmentFragment[],
): Exploration[] =>
  compact(
    input.map((e) => {
      return convertExplorationTypes(e);
    }),
  );

export const convertAlertConfigurationTypes = (
  input?: GraphQLAlertConfiguration | null,
): AlertConfiguration | undefined =>
  isNil(input)
    ? undefined
    : {
        alertConfigurationId: input.alertConfigurationId,
        name: input.name,
        message: input.message,
        pipeline: convertPipelineTypes(input.pipeline) as DereferencedPipeline,
        keyFields: input.keyFields,
        excludedFields: input.excludedFields,
        notificationConfig: input.notificationConfig,
        createdAt: input.createdAt,
      };

export const convertPropertyValueFormat = (
  format: string | undefined | null,
): common.PropertyValueFormat | undefined => {
  if (isNil(format)) {
    return undefined;
  }
  const res = common.propertyValueFormat.safeParse(format);
  if (res.success) {
    return res.data;
  }
  return undefined;
};

export const convertPropertyValueType = (
  type: string | undefined | null,
): model.PropertyType | null => {
  const res = model.propertyType.safeParse(type);
  if (res.success) {
    return res.data;
  }
  return null;
};

const isExplorationMessage = (msg: ConversationMessage): msg is ConversationExplorationMessage =>
  msg.type === 'exploration';

export const convertChatMessageType = (
  message: ConversationMessage,
): ChatMessage | ChatExploration => {
  if (isExplorationMessage(message)) {
    const exploration = convertExplorationTypes(message.exploration);

    if (exploration === undefined) {
      throw new Error('Missing exploration in TTQ response');
    }

    return {
      id: message.id,
      type: 'exploration' as const,
      role: 'assistant' as const,
      exploration,
      jobId: message.jobId ?? undefined,
    };
  }

  if (message.role === 'assistant') {
    return {
      id: message.id,
      type:
        message.type === 'clarifying_question'
          ? ('clarifying_question' as const)
          : ('progress' as const),
      role: 'assistant' as const,
      message: message.message,
      jobId: message.jobId ?? undefined,
    };
  }

  return {
    id: message.id,
    type: message.type,
    role: 'user' as const,
    message: message.message,
    jobId: message.jobId ?? undefined,
    ...(message.type !== 'clarifying_answer'
      ? { sources: message.enabledSources ?? [], research: message.research ?? false }
      : {}),
  } as ChatUserMessage;
};

export const convertConversationType = (input?: Conversation) => {
  return isNil(input) || isEmpty(input)
    ? null
    : {
        conversationId: input.conversationId,
        messages: (input.messages ?? []).map(convertChatMessageType),
        jobs: (input.jobs ?? []).map((job) => ({
          jobId: job.jobId,
          parentJobId: job.parentJobId ?? undefined,
          channel: job.channel ?? undefined,
          progress: (job.progress ?? []) as ConversationJobProgress[],
          createdAt: job.createdAt,
        })),
      };
};
